/**
 * Statusleiste "Prüfung und Freigabe" (D-03): bei allen fuenf Auswertungen dieselbe Zeile Punkte
 * (Meldestand, Sperren, Rueckrechenbarkeit, Handentscheidungen) plus Uebertragen/CSV/Vorschau.
 * Ein Punkt klappt genau ein Detail darunter auf. Uebertragen ist eine bewusste, bestaetigte
 * Handlung, nie automatisch (D-21) -- es gibt keinen Punkt fuer einen Abschlusszustand des
 * Zeitraums (D-23).
 *
 * Der Sperrteil (`locks`) ist optional (siehe <behavior> in 39-11-PLAN.md): fehlt er, bleiben
 * Meldestand und alle drei Knoepfe, die uebrigen Punkte entfallen. Alle Zahlen und Texte kommen
 * aus den Props -- keine eigene Zaehlung im Frontend.
 */
import { useState, type CSSProperties, type ReactNode } from 'react';
import { isAxiosError } from 'axios';
import { Button, Card, Group } from '@mantine/core';
import { IconLock } from '@tabler/icons-react';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { evaluationTableApi } from '../../services/evaluationTableApi';
import { statusPalette } from '../../theme/colors';
import { downloadBlob } from '../../utils/downloadBlob';
import { getApiErrorMessage } from '../../utils/errorHandling';
import { TransferPreview } from './TransferPreview';
import type { EvaluationType } from '../../types/evaluations';
import type {
  ReportedInfo,
  StatusBarInfo,
  TransferPreview as TransferPreviewData,
} from '../../types/evaluationTable';
import classes from './evaluationTable.module.css';

const RECOVERABLE_LIMIT = 8;
const DECISIONS_LIMIT = 8;

const barColorVars = {
  '--eval-positive-color': statusPalette.freigegeben.fg,
  '--eval-mid-color': statusPalette.eingereicht.fg,
} as CSSProperties;

// Sperren sind der Normalfall einer geprueften Periode, kein Befund -- deshalb `neutral`, nicht rot.
type ChipTone = 'ok' | 'mid' | 'neutral';

const CHIP_TONE_CLASS: Record<ChipTone, string> = {
  ok: classes.chipOk,
  mid: classes.chipMid,
  neutral: classes.chipNeutral,
};

interface ChipData {
  key: string;
  tone: ChipTone;
  icon: ReactNode;
  label: string;
  detail: ReactNode | null;
}

function cx(...values: Array<string | false | undefined>): string {
  return values.filter(Boolean).join(' ');
}

function wordForm(count: number, singular: string, plural: string): string {
  return count === 1 ? singular : plural;
}

function reportedChipData(reported: ReportedInfo): ChipData {
  const ok = reported.reported === reported.expected;
  const missing = reported.missingPlants.length;
  return {
    key: 'reported',
    tone: ok ? 'ok' : 'mid',
    icon: ok ? '✓' : '!',
    label: `${reported.reported} von ${reported.expected} genehmigt`,
    detail: ok ? null : (
      <span className={classes.hint}>
        {missing} {wordForm(missing, 'Meldung', 'Meldungen')} noch nicht genehmigt:{' '}
        {reported.missingPlants.join(', ')}
      </span>
    ),
  };
}

function lockedChipData(locks: StatusBarInfo): ChipData {
  const { lockedCount, lockedByRuleCount, lockedManualCount } = locks;
  const parts = [
    lockedByRuleCount
      ? `${lockedByRuleCount} nach Regel (mindestens 5 Unternehmen, höchstens 80 %)`
      : null,
    lockedManualCount ? `${lockedManualCount} von Hand` : null,
  ].filter((part): part is string => Boolean(part));

  return {
    key: 'locked',
    tone: lockedCount ? 'neutral' : 'ok',
    icon: lockedCount ? <IconLock size={13} /> : '✓',
    label: lockedCount ? `${lockedCount} gesperrt` : 'nichts gesperrt',
    detail: (
      <span className={classes.hint}>
        {lockedCount
          ? parts.join(', ')
          : 'mindestens 5 Unternehmen, kein Anteil über 80 %'}
      </span>
    ),
  };
}

function recoverableChipData(
  locks: StatusBarInfo,
  onHighlight?: (cellKeys: string[], anchorCellKey: string) => void
): ChipData {
  const entries = locks.recoverable;
  const visible = entries.slice(0, RECOVERABLE_LIMIT);
  const rest = entries.length - visible.length;

  return {
    key: 'recoverable',
    tone: entries.length ? 'mid' : 'ok',
    icon: entries.length ? '!' : '✓',
    label: entries.length
      ? `${entries.length} zurückrechenbar`
      : 'nicht zurückrechenbar',
    detail: entries.length ? (
      <>
        {visible.map((entry) => (
          <span key={entry.cellKey} className={classes.hint}>
            {entry.label}: {entry.description}{' '}
            <button
              type="button"
              className={classes.linkButton}
              onClick={() =>
                onHighlight?.(entry.highlightCellKeys, entry.cellKey)
              }
            >
              zeigen
            </button>
          </span>
        ))}
        {rest > 0 && <span className={classes.hint}>und {rest} weitere</span>}
        <span className={classes.hintMuted}>
          Eine weitere Zelle derselben Summe sperren – welche, entscheiden Sie.
        </span>
      </>
    ) : (
      <span className={classes.hint}>
        geprüft über Summen und Beziehungen zwischen den Auswertungen
      </span>
    ),
  };
}

interface DecisionsChipParams {
  locks: StatusBarInfo;
  onReset: (cellKey: string) => void;
}

function decisionsChipData({
  locks,
  onReset,
}: DecisionsChipParams): ChipData | null {
  const entries = locks.decisions;
  if (entries.length === 0) {
    return null;
  }
  const visible = entries.slice(0, DECISIONS_LIMIT);
  const rest = entries.length - visible.length;

  return {
    key: 'decisions',
    tone: 'mid',
    icon: '✎',
    label: `${entries.length} von Hand`,
    detail: (
      <>
        {visible.map((entry) => (
          <span key={entry.cellKey} className={classes.hint}>
            {entry.label}:{' '}
            {entry.state === 'locked' ? 'gesperrt' : 'freigegeben'}
            {entry.note ? ` – „${entry.note}“` : ''}{' '}
            <button
              type="button"
              className={classes.linkButton}
              onClick={() => onReset(entry.cellKey)}
            >
              zurücknehmen
            </button>
          </span>
        ))}
        {rest > 0 && <span className={classes.hint}>und {rest} weitere</span>}
      </>
    ),
  };
}

export interface ReleaseStatusBarProps {
  evaluationType: EvaluationType;
  year: number;
  period: number;
  title: string;
  reported: ReportedInfo;
  locks?: StatusBarInfo;
  onChanged: () => void;
  onHighlight?: (cellKeys: string[], anchorCellKey: string) => void;
  /** Letztes Kind der Karte, nach Chip-Detail und Vorschau -- z. B. die Link-Zeile (D-09). */
  footer?: ReactNode;
}

export function ReleaseStatusBar({
  evaluationType,
  year,
  period,
  title,
  reported,
  locks,
  onChanged,
  onHighlight,
  footer,
}: ReleaseStatusBarProps) {
  const [openChip, setOpenChip] = useState<number | null>(null);
  const [transferLoading, setTransferLoading] = useState(false);
  const [csvLoading, setCsvLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState<TransferPreviewData | null>(
    null
  );

  const handleResetDecision = (cellKey: string) => {
    evaluationTableApi
      .deleteCellDecision({ evaluationType, year, period, cellKey })
      .then(onChanged)
      .catch((err: unknown) => {
        notifications.show({
          color: 'red',
          title: 'Nicht gespeichert',
          message: getApiErrorMessage(
            err,
            'Die Änderung konnte nicht gespeichert werden.'
          ),
        });
      });
  };

  const chips: ChipData[] = [reportedChipData(reported)];
  if (locks) {
    chips.push(lockedChipData(locks), recoverableChipData(locks, onHighlight));
    const decisionsChip = decisionsChipData({
      locks,
      onReset: handleResetDecision,
    });
    if (decisionsChip) {
      chips.push(decisionsChip);
    }
  }

  const handleTransferConfirmed = async () => {
    setTransferLoading(true);
    try {
      const result = await evaluationTableApi.transfer(
        evaluationType,
        year,
        period
      );
      notifications.show({
        message: `${title} übertragen (${result.rowCount} Zeilen)`,
      });
      onChanged();
    } catch (err) {
      // 41-02: Gesendet, aber das Protokoll liess sich nicht schreiben -- der Server meldet das mit
      // `transferSucceeded`. Dann nicht "Nicht übertragen" titeln, sonst wird erneut übertragen.
      const transferSucceeded =
        isAxiosError(err) && err.response?.data?.transferSucceeded === true;
      notifications.show({
        color: transferSucceeded ? 'orange' : 'red',
        title: transferSucceeded ? 'Übertragen, aber nicht protokolliert' : 'Nicht übertragen',
        message: getApiErrorMessage(err, 'Die Übertragung ist fehlgeschlagen.'),
      });
      // Der Fehlversuch wird seit 41-02 protokolliert und aendert damit den Uebertragungsstand
      // (Periodenleiste, Link-Zeile) -- onChanged() gilt deshalb auch im Fehlerzweig.
      onChanged();
    } finally {
      setTransferLoading(false);
    }
  };

  const handleTransferClick = () => {
    const lockSentence = locks
      ? ` ${locks.lockedCount} gesperrte Werte werden leer übertragen.`
      : '';
    modals.openConfirmModal({
      title: 'An das Statistik-Portal übertragen?',
      children: `${title} wird übertragen.${lockSentence} Eine frühere Übertragung dieses Zeitraums wird überschrieben.`,
      labels: { confirm: 'Übertragen', cancel: 'Abbrechen' },
      onConfirm: () => void handleTransferConfirmed(),
    });
  };

  const handleCsvExport = async () => {
    setCsvLoading(true);
    try {
      const { blob, filename } = await evaluationTableApi.downloadCsv(
        evaluationType,
        year,
        period
      );
      downloadBlob(blob, filename);
      notifications.show({
        message: `${filename} exportiert – gleiche Werte wie die Übertragung`,
      });
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Nicht exportiert',
        message: getApiErrorMessage(err, 'Der CSV-Export ist fehlgeschlagen.'),
      });
    } finally {
      setCsvLoading(false);
    }
  };

  const handlePreviewToggle = async () => {
    if (previewOpen) {
      setPreviewOpen(false);
      return;
    }
    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      const data = await evaluationTableApi.getTransferPreview(
        evaluationType,
        year,
        period
      );
      setPreviewData(data);
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Vorschau nicht geladen',
        message: getApiErrorMessage(
          err,
          'Die Vorschau konnte nicht geladen werden.'
        ),
      });
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <Card withBorder radius="md" p="md" mb="md" style={barColorVars}>
      <div className={classes.chipRow}>
        <h2>Prüfung und Freigabe</h2>
        <Group gap={6} className={classes.chips}>
          {chips.map((chip, index) => {
            const hasDetail = chip.detail !== null;
            const isOpen = hasDetail && openChip === index;
            return (
              <button
                key={chip.key}
                type="button"
                disabled={!hasDetail}
                aria-expanded={hasDetail ? isOpen : undefined}
                className={cx(
                  classes.chip,
                  CHIP_TONE_CLASS[chip.tone],
                  isOpen && classes.chipOpen
                )}
                onClick={() => setOpenChip(isOpen ? null : index)}
              >
                <span className={classes.chipIcon} aria-hidden="true">
                  {chip.icon}
                </span>
                {chip.label}
              </button>
            );
          })}
        </Group>
        <Group gap={6}>
          <Button
            size="xs"
            loading={transferLoading}
            onClick={handleTransferClick}
          >
            An Statistik-Portal übertragen
          </Button>
          <Button
            size="xs"
            variant="light"
            loading={csvLoading}
            onClick={() => void handleCsvExport()}
          >
            Als CSV exportieren
          </Button>
          <Button
            size="xs"
            variant="light"
            loading={previewLoading}
            onClick={() => void handlePreviewToggle()}
          >
            {previewOpen ? 'Vorschau schließen' : 'Vorschau der Übertragung'}
          </Button>
        </Group>
      </div>
      {openChip !== null && chips[openChip]?.detail && (
        <div className={classes.chipDetail}>
          <div>{chips[openChip].detail}</div>
        </div>
      )}
      {previewOpen && previewData && <TransferPreview preview={previewData} />}
      {footer}
    </Card>
  );
}
